import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { Alert, AlertColor, Snackbar } from '@mui/material';

type Toast = {
  id: number;
  severity: AlertColor;
  message: string;
  // Controls the Snackbar's open prop. We flip this to false (rather than
  // removing the toast from the array) so MUI runs its exit transition;
  // the toast is removed from the array in onExited.
  open: boolean;
};

type ToastContextValue = {
  show: (message: string, severity?: AlertColor) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
};

const noop = () => {};

const ToastContext = createContext<ToastContextValue>({
  show: noop,
  success: noop,
  error: noop,
  info: noop,
  warning: noop,
});

const AUTO_HIDE_MS = 4000;
// Vertical offset of the topmost toast from the viewport edge. Matches
// MUI's default Snackbar top spacing so the first toast sits where it
// would have without our stack overrides.
const STACK_TOP_PX = 24;
// Per-toast slot height including a small gap. Tuned so adjacent filled
// Alerts (single-line) read as separate cards rather than one block.
const STACK_GAP_PX = 64;

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const show = useCallback(
    (message: string, severity: AlertColor = 'success') => {
      idRef.current += 1;
      const id = idRef.current;
      setToasts((prev) => [...prev, { id, severity, message, open: true }]);
    },
    []
  );

  const success = useCallback(
    (message: string) => show(message, 'success'),
    [show]
  );
  const error = useCallback(
    (message: string) => show(message, 'error'),
    [show]
  );
  const info = useCallback((message: string) => show(message, 'info'), [show]);
  const warning = useCallback(
    (message: string) => show(message, 'warning'),
    [show]
  );

  // Two-phase dismissal: setOpenFalse triggers the slide-out animation,
  // remove unmounts the Snackbar after the animation completes. This lets
  // toasts below smoothly shift up via the `top` CSS transition.
  const setOpenFalse = useCallback((id: number) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, open: false } : t))
    );
  }, []);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ show, success, error, info, warning }}>
      {children}
      {toasts.map((t, index) => (
        <Snackbar
          key={t.id}
          open={t.open}
          autoHideDuration={AUTO_HIDE_MS}
          onClose={(_event, reason) => {
            // Ignore stray clicks elsewhere on the page so toasts only
            // dismiss via timeout, escape, or the explicit close button.
            if (reason === 'clickaway') return;
            setOpenFalse(t.id);
          }}
          TransitionProps={{ onExited: () => remove(t.id) }}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          // Inline style wins over MUI's class-based default `top`, so each
          // toast slots into its own row. The `top` transition makes the
          // remaining toasts glide up when one above them is dismissed.
          style={{
            top: STACK_TOP_PX + index * STACK_GAP_PX,
            transition: 'top 300ms ease',
          }}
        >
          <Alert
            severity={t.severity}
            variant='filled'
            onClose={() => setOpenFalse(t.id)}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
